import { useState, useEffect, useRef, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Modal, Spin } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import {
  CheckOutlined,
  LoadingOutlined,
  CreditCardOutlined,
  TeamOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { TablesEmptyState } from "../TablesEmptyState";
import {
  useActivePaymentMethods,
  usePaymentMethods,
} from "../../../../../hooks/usePaymentMethods";
import { usePrinterNotifications } from "../../../../../hooks/usePrinterNotifications";
import { useAppDispatch, useAppSelector } from "../../../../../store/hooks";
import { getBills } from "../../../../../api/bills";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { useDiscountClients } from "../../../../../hooks/useDiscounts";
import { useTranslation } from "react-i18next";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import { useViewport } from "../../../../../hooks/useViewport";
import {
  ModalHeader,
  ModalFooter,
  GhostButton,
  PrimaryButton,
} from "../../modalPrimitives";
import { CashChangePanel } from "./CashChangePanel";
import { MixedPaymentPanel } from "./MixedPaymentPanel";
import {
  runBackgroundBillClose,
  type CloseBillSelection,
} from "./backgroundBillClose";
import { useClosingBills } from "./closingBills";
import type { BillOut } from "../../../../../api/bills";
import { BillCustomerBanner } from "../clients/BillCustomerBanner";

interface CloseTableModalProps {
  open: boolean;
  onClose: () => void;
  billId: number;
  tableName: string;
  totalAmount: string;
  operatorName: string;
  canPaymentCash?: boolean;
  canPaymentCard?: boolean;
  canCloseBill1?: boolean;
  customerId?: number | null;
  /** Сметката, която се закрива — за лентата „закачен клиент" под хедъра
   *  (име, процент, спестена сума) и като кутийка в списъка с маси, докато
   *  закриването чака принтера. Без нея лентата не се показва. */
  bill?: BillOut | null;
  // "retry" — сметката вече е закрита, но е без касова бележка (total = 0).
  // Модалът е БУКВАЛНО същият (същите методи, същите три клика, същите
  // текстове); сменя се само endpoint-ът, защото сметката вече е закрита.
  mode?: "close" | "retry";
  /** При „Закриване веднага след поръчка" сервитьорът няма кога да мине през
   *  „Опции → Клиенти" преди закриването — подаден ли е handler-ът, футърът
   *  показва среден бутон „Клиенти", който отваря списъка ВЪРХУ този модал.
   *  Изборът се отразява тук през customerId (сметката се презарежда). */
  onOpenClients?: () => void;
  /** Отворен слой над модала (списъкът с клиенти) — спира Enter/Esc/стрелките
   *  тук, иначе Enter от избора на клиент би закрил сметката. */
  keyboardDisabled?: boolean;
}

export const CloseTableModal = ({
  open,
  onClose,
  billId,
  tableName,
  totalAmount,
  operatorName,
  canPaymentCash = true,
  canPaymentCard = true,
  canCloseBill1 = false,
  customerId = null,
  bill = null,
  mode = "close",
  onOpenClients,
  keyboardDisabled = false,
}: CloseTableModalProps) => {
  const isRetry = mode === "retry";
  const { t, i18n } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const { data: rawMethods = [], isLoading } = useActivePaymentMethods();
  const { data: allMethods = [] } = usePaymentMethods();
  // id=3 (Смесено) се показва само ако имаме конфигурирани и id=1 (В брой), и id=2 (С карта).
  // Ако backend не е върнал id=3 в активния списък, синтезираме го от мастър списъка.
  const methods = useMemo(() => {
    const hasCash = rawMethods.some((m) => m.id === 1);
    const hasCard = rawMethods.some((m) => m.id === 2);
    const withoutMixed = rawMethods.filter((m) => m.id !== 3);
    if (!hasCash || !hasCard) return withoutMixed;
    const mixed =
      rawMethods.find((m) => m.id === 3) ?? allMethods.find((m) => m.id === 3);
    if (!mixed) return withoutMixed;
    const cardIdx = withoutMixed.findIndex((m) => m.id === 2);
    const insertAt = cardIdx >= 0 ? cardIdx + 1 : withoutMixed.length;
    return [
      ...withoutMixed.slice(0, insertAt),
      mixed,
      ...withoutMixed.slice(insertAt),
    ];
  }, [rawMethods, allMethods]);
  const message = useOrderingMessage();
  const { notifyPrinterError, notifyPrinterTimeout } = usePrinterNotifications();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const currentUserId = useAppSelector((state) => state.auth.userId);
  const { closingBillIds } = useClosingBills();

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    number | null
  >(null);
  // Само проверката на сумата преди закриването (спинер на бутона) — самото
  // закриване чака принтера ВЪВ ФОН, със спинер на кутийката на масата.
  const [isClosing, setIsClosing] = useState(false);
  const [mixedCashAmount, setMixedCashAmount] = useState<number>(0);
  const [mixedCardAmount, setMixedCardAmount] = useState<number>(0);
  const [cashClickCount, setCashClickCount] = useState(0);
  // Полученото за ресто-помощника при „В брой" — чисто визуално, не влиза
  // в payload-а към closeBill.
  const [cashReceived, setCashReceived] = useState("");

  const cashOnlyMode = canCloseBill1 && cashClickCount >= 3;

  // Плащане по сметка (id=4) се ПОКАЗВА само при избран клиент с флаг
  // can_pay_on_account (org DB) — без такъв клиент методът е скрит изцяло.
  const { data: discountData } = useDiscountClients(open);
  const hasCustomer = customerId != null;
  // Редовете „Сума без отстъпка / Отстъпка на клиента" над „Общо" в лявата
  // колона — само при закачен клиент (BillTotalsBreakdown крие 0%).
  const clientDiscount = bill?.discount_applied
    ? {
        amount: parseFloat(bill.total_discount || "0") || 0,
        percent: bill.discount_applied.percent ?? bill.discount_percent ?? 0,
      }
    : null;
  const canOnAccount =
    hasCustomer &&
    (discountData?.items.find((c) => c.id === customerId)
      ?.can_pay_on_account ??
      false);

  // Ако клиентът отпадне/няма право докато id=4 е избран — върни към cash
  useEffect(() => {
    if (selectedPaymentMethodId === 4 && !canOnAccount) {
      setSelectedPaymentMethodId(1);
    }
  }, [canOnAccount, selectedPaymentMethodId]);

  const handleCloseRef = useRef<() => void>(() => {});
  // Кликнат ли е метод ръчно, авто-дефолтът долу спира да пипа избора
  const userPickedMethodRef = useRef(false);

  // Дефолтен метод при отваряне: „По сметка" (4) при избран клиент с правото,
  // иначе „В брой" (1). Данните за клиента идват async → ефектът може да
  // превключи 1 → 4 малко след отварянето, но САМО преди ръчен избор.
  useEffect(() => {
    if (!open || methods.length === 0 || userPickedMethodRef.current) return;
    const defaultMethodId =
      canOnAccount && methods.some((m) => m.id === 4) ? 4 : 1;
    if (
      methods.some((m) => m.id === defaultMethodId) &&
      selectedPaymentMethodId !== defaultMethodId
    ) {
      setSelectedPaymentMethodId(defaultMethodId);
    }
  }, [open, methods, canOnAccount, selectedPaymentMethodId]);

  useEffect(() => {
    if (!open) {
      setSelectedPaymentMethodId(null);
      setMixedCashAmount(0);
      setMixedCardAmount(0);
      setCashClickCount(0);
      setCashReceived("");
      userPickedMethodRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (!open || !isDesktop || isLoading || keyboardDisabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLElement &&
        (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")
      )
        return;

      if (e.key === "Enter") {
        e.preventDefault();
        if (!isClosing && selectedPaymentMethodId) handleCloseRef.current();
      } else if (e.key === "Escape") {
        e.preventDefault();
        if (!isClosing) onClose();
      } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();
        const availableMethods = methods.filter((m) => {
          if (m.id === 1) return canPaymentCash;
          if (m.id === 2) return canPaymentCard;
          if (m.id === 3) return canPaymentCash && canPaymentCard;
          if (m.id === 4) return canOnAccount;
          return true;
        });
        if (availableMethods.length === 0) return;
        const currentIndex = availableMethods.findIndex(
          (m) => m.id === selectedPaymentMethodId,
        );
        const newIndex =
          e.key === "ArrowDown"
            ? (currentIndex + 1) % availableMethods.length
            : currentIndex <= 0
              ? availableMethods.length - 1
              : currentIndex - 1;
        const newMethodId = availableMethods[newIndex].id;
        // Смяната на метода със стрелките също прекъсва последователните
        // кликове на „В брой"
        if (newMethodId !== 1) setCashClickCount(0);
        setSelectedPaymentMethodId(newMethodId);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    open,
    isDesktop,
    isLoading,
    keyboardDisabled,
    isClosing,
    selectedPaymentMethodId,
    methods,
    canPaymentCash,
    canPaymentCard,
    canOnAccount,
    onClose,
  ]);

  const handleClose = () => handleCloseRef.current();

  handleCloseRef.current = async () => {
    if (!selectedPaymentMethodId) {
      message.warning(t("closeTable.selectPaymentWarning"));
      return;
    }
    // Закриването на тази сметка още чака принтера (кутийката ѝ се върти) —
    // второ (напр. „Фискализирай" от „Възстановяване" с друг метод) би
    // издало втори бон. Докато модалът блокираше екрана, това не ставаше.
    if (closingBillIds.has(billId)) {
      message.warning(t("closeTable.stillClosing"));
      return;
    }

    setIsClosing(true);
    try {
      // Подаденият тотал може да е остарял (друг сервитьор, сторно) — при
      // самото натискане теглим свежите сметки. fetchQuery пълни и кеша
      // ["bills"], така че показаната сума се опреснява навсякъде веднага.
      const freshBills = await queryClient.fetchQuery({
        queryKey: ["bills"],
        queryFn: getBills,
        staleTime: 0,
      });
      const freshBill = freshBills.find((bill) => bill.id === billId);
      // Липсва ли сметката в свежия списък, продължаваме със старите данни —
      // BE е авторитетът и връща 409, който се обработва при закриването.
      const freshTotal = parseFloat(freshBill?.total_amount ?? totalAmount);
      if (Math.abs(freshTotal - parseFloat(totalAmount)) > 0.005) {
        message.warning(
          t("closeTable.totalChanged", { total: freshTotal.toFixed(2) }),
        );
        return;
      }

      if (selectedPaymentMethodId === 3) {
        const sum = mixedCashAmount + mixedCardAmount;
        if (Math.abs(sum - freshTotal) > 0.01) {
          message.warning(
            t("closeTable.sumMismatch", {
              total: freshTotal.toFixed(2),
              entered: sum.toFixed(2),
            }),
          );
          return;
        }
      }

      // Тялото е едно и също за двата пътя — разликата е само endpoint-ът:
      // затворената сметка не може да се затвори втори път.
      const closeSelection: CloseBillSelection = {
        billId,
        paymentMethodId: selectedPaymentMethodId,
        mixedCashAmount:
          selectedPaymentMethodId === 3 ? mixedCashAmount : undefined,
        mixedCardAmount:
          selectedPaymentMethodId === 3 ? mixedCardAmount : undefined,
        total: cashOnlyMode || selectedPaymentMethodId === 4 ? 0 : undefined,
      };
      // Оттук модалът не чака принтера: закриването тръгва във фон (същата
      // заявка, същите таймаути), спинерът минава на кутийката на масата, а
      // изходът излиза като известие с името ѝ (backgroundBillClose.ts).
      void runBackgroundBillClose({
        selection: closeSelection,
        isRetry,
        tableName,
        snapshot: freshBill ?? bill,
        userId: currentUserId,
        dispatch,
        queryClient,
        message,
        notifyPrinterError,
        notifyPrinterTimeout,
        t,
      });
      onClose();
    } catch (error) {
      // Тук стига само провалена проверка на сумата (GET /bills) — нищо не е
      // закрито и модалът остава отворен за повторен опит.
      message.error(getApiErrorMessage(error, t("closeTable.closeError")));
      console.error("Error closing bill:", error);
    } finally {
      setIsClosing(false);
    }
  };

  const selectedMethod = methods.find((m) => m.id === selectedPaymentMethodId);
  // „Смесено" на широк екран: бутонът е неактивен, докато сборът не покрие
  // сметката — визуално огледало на проверката в handleClose (тя си остава и
  // пази Enter пътя); телефонът остава на старото поведение с предупреждение.
  const mixedIncomplete =
    !isNarrow &&
    selectedPaymentMethodId === 3 &&
    Math.abs(mixedCashAmount + mixedCardAmount - parseFloat(totalAmount)) >
      0.01;
  // Широк модал на голям екран — двете тонирани колони искат въздух.
  const width = isMobile ? undefined : isTablet ? 560 : 880;

  return (
    <Modal
      open={open}
      // Клик по маската по време на проверката на сумата не затваря — иначе
      // закриването би тръгнало след затворен модал, без сервитьорът да
      // е видял дали сумата не се е сменила.
      onCancel={() => {
        if (!isClosing) onClose();
      }}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div
        className="bg-pos-card font-pos-sans text-pos-ink"
        onClick={() => {
          if (cashOnlyMode) setCashClickCount(0);
        }}
      >
        <ModalHeader
          icon={<CreditCardOutlined />}
          title={t("closeTable.table") + " " + tableName}
          subtitle={`${t("closeTable.operator")} ${operatorName}`}
          onClose={() => {
            if (!isClosing) onClose();
          }}
          isNarrow={isNarrow}
          tone="panel"
        />

        {/* Закачен клиент — лента на цяла ширина под хедъра */}
        {bill?.discount_applied && (
          <BillCustomerBanner
            bill={bill}
            compact={isNarrow}
            // Същият хоризонтален падинг като ModalHeader — иконата и X-ът
            className={isNarrow ? "px-4" : "px-[22px]"}
          />
        )}

        {isLoading ? (
          <div className="flex items-center justify-center px-6 py-12">
            <Spin
              indicator={
                <LoadingOutlined className="text-[36px] text-pos-accent" spin />
              }
            />
          </div>
        ) : (
          <>
            <div
              className={
                isNarrow
                  ? "flex flex-col gap-4 px-4 py-3.5"
                  : // Широк екран: две тонирани колони, допрени на вертикален
                    // разделител — паддингът е ВЪТРЕ в колоните, не на обвивката
                    "flex items-stretch"
              }
            >
              {/* Лява колона: на телефон е СТАРИЯТ изглед 1:1 (метод + сума,
                  без ресто помощник); панелите по метод са само за широк екран */}
              <div
                className={
                  isNarrow
                    ? "flex min-w-0 flex-1 flex-col"
                    : "flex w-[38%] min-w-0 flex-col bg-pos-canvas px-5 py-5"
                }
              >
                {isNarrow ? (
                  <>
                    <div className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[1.2px] text-pos-muted">
                      {t("closeTable.paymentMethod")}
                    </div>
                    <div className="truncate text-base font-bold tracking-[-0.3px] text-pos-ink">
                      {selectedMethod
                        ? i18n.language === "en"
                          ? selectedMethod.name_en
                          : selectedMethod.name_bg
                        : t("closeTable.notSelected")}
                    </div>
                    <div className="mt-1.5 font-pos-mono text-[32px] font-bold leading-none tracking-[-1.2px] text-pos-accent [font-variant-numeric:tabular-nums]">
                      {parseFloat(totalAmount).toFixed(2)}
                    </div>
                  </>
                ) : selectedPaymentMethodId === 1 ? (
                  /* „В брой" — помощникът за ресто (само визуален) */
                  <CashChangePanel
                    totalAmount={totalAmount}
                    received={cashReceived}
                    onReceivedChange={setCashReceived}
                    disabled={isClosing}
                    discount={clientDiscount}
                  />
                ) : selectedPaymentMethodId === 3 ? (
                  /* „Смесено" — разпределението, В брой и С карта едно под друго */
                  <MixedPaymentPanel
                    large
                    discount={clientDiscount}
                    totalAmount={totalAmount}
                    cashAmount={mixedCashAmount}
                    cardAmount={mixedCardAmount}
                    onCashChange={(cash) => {
                      setMixedCashAmount(cash);
                      const remaining = parseFloat(totalAmount) - cash;
                      setMixedCardAmount(Math.max(0, remaining));
                    }}
                    onCardChange={(card) => {
                      setMixedCardAmount(card);
                      const remaining = parseFloat(totalAmount) - card;
                      setMixedCashAmount(Math.max(0, remaining));
                    }}
                    disabled={isClosing}
                  />
                ) : (
                  <>
                    <div className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[1.2px] text-pos-muted">
                      {t("closeTable.totalToPay")}
                    </div>
                    <div
                      className={`font-pos-mono font-bold leading-none tracking-[-1.2px] text-pos-accent [font-variant-numeric:tabular-nums] ${
                        isNarrow ? "text-[32px]" : "text-[40px]"
                      }`}
                    >
                      {parseFloat(totalAmount).toFixed(2)}{" "}
                      <span className="text-base text-pos-muted">EUR</span>
                    </div>
                    {/* Подсказка в дъното на колоната — само при „С карта" */}
                    {selectedPaymentMethodId === 2 && (
                      <div className="mt-auto pt-6 text-[13px] font-medium text-pos-soft2">
                        {t("closeTable.cardTerminalHint")}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Дясна колона: методите на плащане — по-топъл тон от лявата */}
              <div
                className={
                  isNarrow
                    ? "min-w-0 flex-1"
                    : "min-w-0 flex-1 border-l border-pos-line2 bg-pos-marked px-5 py-5"
                }
              >
                <div className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[1.2px] text-pos-muted">
                  {t("closeTable.selectMethod")}
                </div>
                {methods.length === 0 ? (
                  <div>
                    <TablesEmptyState
                      icon={<WalletOutlined />}
                      title={t("closeTable.noMethodsTitle")}
                      description={t("closeTable.noMethodsDescription")}
                    />
                  </div>
                ) : (
                  <div
                    className={`flex flex-col ${isNarrow ? "gap-1.5" : "gap-2.5"}`}
                  >
                    {/* „По сметка" (4) е СКРИТ без клиент с правото */}
                    {methods
                      .filter((m) => m.id !== 4 || canOnAccount)
                      .map((method) => {
                        const isSelected =
                          selectedPaymentMethodId === method.id;
                        const isDisabled =
                          (method.id === 1 && !canPaymentCash) ||
                          (method.id === 2 && !canPaymentCard) ||
                          (method.id === 3 &&
                            (!canPaymentCash || !canPaymentCard));
                        const isHidden = cashOnlyMode && method.id !== 1;

                        return (
                          <button
                            key={method.id}
                            type="button"
                            onClick={(e) => {
                              if (isDisabled || isHidden) return;
                              userPickedMethodRef.current = true;
                              if (method.id === 1 && canCloseBill1) {
                                e.stopPropagation();
                                setCashClickCount((c) => c + 1);
                              } else {
                                // Броят се само ПОСЛЕДОВАТЕЛНИ кликове на
                                // „В брой" — друг метод нулира броенето
                                setCashClickCount(0);
                              }
                              setSelectedPaymentMethodId(method.id);
                            }}
                            disabled={isDisabled}
                            /* Неактивен ред (напр. „По сметка" без клиент) —
                               БЕЗ топъл фон, сив текст + намалена опасити, за
                               да чете като недостъпен, а не като избран. */
                            className={`flex items-center justify-between gap-2 border text-left transition-colors ${
                              isNarrow
                                ? "rounded-[10px] px-3 py-2.5"
                                : "rounded-xl px-4 py-4"
                            } ${
                              isHidden ? "invisible" : "visible"
                            } ${
                              isSelected
                                ? "border-pos-accent/30 bg-pos-ready-bg"
                                : "border-pos-line2 bg-pos-card"
                            } ${
                              isDisabled
                                ? "cursor-not-allowed opacity-55"
                                : "cursor-pointer"
                            }`}
                          >
                            <span
                              className={`truncate font-semibold ${
                                isNarrow ? "text-[13.5px]" : "text-[15px]"
                              } ${
                                isDisabled
                                  ? "text-pos-muted"
                                  : isSelected
                                    ? "text-pos-accent-ink"
                                    : "text-pos-ink"
                              }`}
                            >
                              {i18n.language === "en"
                                ? method.name_en
                                : method.name_bg}
                            </span>
                            <span
                              className={`inline-flex shrink-0 items-center justify-center rounded-full border text-white ${
                                isNarrow ? "h-5 w-5" : "h-[22px] w-[22px]"
                              } ${
                                isSelected
                                  ? "border-pos-accent bg-pos-accent"
                                  : "border-pos-ctl bg-pos-card"
                              }`}
                            >
                              {isSelected && (
                                <CheckOutlined className="text-[11px]" />
                              )}
                            </span>
                          </button>
                        );
                      })}
                  </div>
                )}
              </div>

              {/* Телефон: смесеното разпределение е ПОД колоните, както в
                  стария изглед (без блока „Общо") */}
              {isNarrow && selectedPaymentMethodId === 3 && (
                <MixedPaymentPanel
                  totalAmount={totalAmount}
                  cashAmount={mixedCashAmount}
                  cardAmount={mixedCardAmount}
                  onCashChange={(cash) => {
                    setMixedCashAmount(cash);
                    const remaining = parseFloat(totalAmount) - cash;
                    setMixedCardAmount(Math.max(0, remaining));
                  }}
                  onCardChange={(card) => {
                    setMixedCardAmount(card);
                    const remaining = parseFloat(totalAmount) - card;
                    setMixedCashAmount(Math.max(0, remaining));
                  }}
                  disabled={isClosing}
                  showTotal={false}
                />
              )}
            </div>

            <ModalFooter isNarrow={isNarrow} tone="panel" narrowFirst>
              <GhostButton
                label={t("common.cancel")}
                onClick={onClose}
                disabled={isClosing}
                shortcut={isDesktop ? "Esc" : undefined}
              />
              {onOpenClients && (
                <GhostButton
                  icon={<TeamOutlined />}
                  label={t("ordering.clients")}
                  onClick={onOpenClients}
                  disabled={isClosing}
                />
              )}
              <PrimaryButton
                label={t("closeTable.closeButton")}
                onClick={handleClose}
                disabled={!selectedPaymentMethodId || mixedIncomplete}
                loading={isClosing}
                shortcut={isDesktop ? "⏎" : undefined}
              />
            </ModalFooter>
          </>
        )}
      </div>
    </Modal>
  );
};
